/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from '@tanstack/react-query';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Message } from 'primereact/message';
import { Button } from 'sharedUi/Button';
import { functionsService, type FunctionMaster } from '../../services/functions.service';
import { referencableIn } from '../../hooks/useProjectScope';
import type { FunctionAttachment } from '../../services/recipient-masters.service';

/**
 * A recipient's resolution chain.
 *
 * Array position IS execution order — there is no ordering column, which is
 * what removes the reorder race. Each step maps its declared inputs to a
 * source, and names the aliases its outputs publish for later steps; the last
 * step's aliases decide the audience.
 *
 * The editor offers a step only the sources that can actually reach it: a
 * FUNCTION_OUTPUT can name an alias published *earlier* in the chain and no
 * later, which is the forward-reference rule saving enforces — surfaced here
 * so it is a greyed-out option rather than a rejection after the fact.
 */
interface FunctionChainEditorProps {
  value: FunctionAttachment[];
  onChange: (chain: FunctionAttachment[]) => void;
  /** The project the notification belongs to — only its own and shared
   *  functions may be attached, which is what the API accepts. */
  projectId?: string | null;
  testId?: string;
}

const SOURCES = [
  { value: 'PAYLOAD', label: 'Payload' },
  { value: 'FUNCTION_OUTPUT', label: 'Earlier step' },
  { value: 'CONTEXT', label: 'Context' },
  { value: 'LITERAL', label: 'Literal' },
];

const FunctionChainEditor = ({ value, onChange, projectId, testId = 'FunctionChainEditor' }: FunctionChainEditorProps) => {
  const { data: functionsPage } = useQuery({
    queryKey: ['functions', 'recipientResolution'],
    // Only resolution functions may be attached — an EVAL_TARGET in a chain is
    // the mistake the capability field exists to prevent.
    queryFn: () => functionsService.list({ page: 1, page_size: 200, capability: 'RECIPIENT_RESOLUTION' }),
    staleTime: 3e5,
  });

  // Active only, which the API enforces too. A deprecated function is refused
  // to NEW attachments — that refusal is the whole point of deprecating rather
  // than archiving it — while a chain already using one keeps running.
  const all = functionsPage?.data || [];
  // A deactivated function is refused as a new attachment, so it is not offered.
  // Its own project's and the shared ones. Another project's would be refused
  // at save with "does not exist in this scope", which reads as a bug.
  const functions = all.filter((f) => f.isActive !== false).filter(referencableIn(projectId));
  const hiddenCount = all.length - functions.length;
  const functionOptions = functions.map((f) => ({ value: f._id, label: f.functionCode }));

  const byId = (fid?: string): FunctionMaster | undefined =>
    functions.find((f) => f._id === String(fid));

  /** Aliases published by every step BEFORE this one. */
  const aliasesBefore = (index: number): string[] => {
    const seen: string[] = [];
    value.slice(0, index).forEach((step) => {
      Object.values(step.outputMapping || {}).forEach((alias) => {
        if (alias) seen.push(alias);
      });
    });
    return seen;
  };

  const update = (index: number, next: FunctionAttachment) =>
    onChange(value.map((s, i) => (i === index ? next : s)));

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= value.length) return;
    const next = [...value];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const setInput = (index: number, name: string, patch: any) => {
    const step = value[index];
    update(index, {
      ...step,
      inputMapping: { ...(step.inputMapping || {}), [name]: { ...((step.inputMapping || {})[name] || { source: 'PAYLOAD' }), ...patch } },
    });
  };

  const setOutputAlias = (index: number, outputName: string, alias: string) => {
    const step = value[index];
    const mapping = { ...(step.outputMapping || {}) };
    if (alias) mapping[outputName] = alias;
    else delete mapping[outputName];
    update(index, { ...step, outputMapping: mapping });
  };

  return (
    <div className="flex flex-col gap-3" data-testid={testId}>
      <div>
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Function chain</label>
        <span className="block text-xs text-gray-400">
          Runs top to bottom — position is execution order. Only the last step's aliases decide the audience.
        </span>
      </div>

      {!functions.length && (
        <Message
          severity="warn"
          className="w-full"
          text="No active functions yet. Create one under Functions first."
        />
      )}

      {functions.length > 0 && hiddenCount > 0 && (
        <p className="text-xs text-gray-400">
          {hiddenCount} deactivated function{hiddenCount === 1 ? '' : 's'} not listed. A step already
          using one keeps working.
        </p>
      )}

      {value.length === 0 && (
        <p className="text-xs text-gray-400">No steps — a DYNAMIC recipient with an empty chain resolves nobody.</p>
      )}

      {value.map((step, index) => {
        const fn = byId(step.functionMasterId);
        const inputs = fn?.inputDefinition || [];
        const outputs = fn?.outputDefinition || [];
        const earlier = aliasesBefore(index);

        return (
          <div key={index} className="border border-line rounded p-3 flex flex-col gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-mono text-gray-400 w-6">{index + 1}.</span>
              <Dropdown
                value={step.functionMasterId}
                options={functionOptions}
                optionLabel="label"
                optionValue="value"
                placeholder="Function"
                className="w-56"
                data-testid={`${testId}.Function.${index}`}
                onChange={(e) => update(index, { functionMasterId: e.value, inputMapping: {}, outputMapping: {} })}
              />
              <InputNumber
                value={step.timeoutSeconds ?? null}
                min={1}
                max={600}
                placeholder="timeout"
                className="w-28"
                data-testid={`${testId}.Timeout.${index}`}
                onValueChange={(e) => update(index, { ...step, timeoutSeconds: e.value ?? null })}
              />
              <div className="flex gap-1 ml-auto">
                <button aria-label="Move up" type="button" title="Move up" disabled={index === 0}
                  className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30"
                  onClick={() => move(index, -1)}>
                  <i className="pi pi-arrow-up text-gray-600 dark:text-gray-300" />
                </button>
                <button aria-label="Move down" type="button" title="Move down" disabled={index === value.length - 1}
                  className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30"
                  onClick={() => move(index, 1)}>
                  <i className="pi pi-arrow-down text-gray-600 dark:text-gray-300" />
                </button>
                <button aria-label="Remove step" type="button" title="Remove step"
                  className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => onChange(value.filter((_, i) => i !== index))}>
                  <i className="pi pi-trash text-red-600 dark:text-red-400" />
                </button>
              </div>
            </div>


            {fn && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-xs text-ink-muted">Inputs</label>
                  {inputs.length ? inputs.map((input) => {
                    const mapped = (step.inputMapping || {})[input.name] || { source: 'PAYLOAD' };
                    return (
                      <div key={input.name} className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-mono text-xs w-28 shrink-0 text-ink">
                          {input.name}{input.isRequired ? ' *' : ''}
                        </span>
                        <Dropdown
                          value={mapped.source}
                          options={SOURCES}
                          optionLabel="label"
                          optionValue="value"
                          className="w-36"
                          data-testid={`${testId}.InSource.${index}.${input.name}`}
                          onChange={(e) => setInput(index, input.name, { source: e.value, path: undefined, value: undefined })}
                        />
                        {mapped.source === 'FUNCTION_OUTPUT' ? (
                          <Dropdown
                            value={mapped.path || ''}
                            options={earlier.map((a) => ({ value: a, label: a }))}
                            placeholder={earlier.length ? 'alias' : 'no earlier alias'}
                            className="flex-1"
                            data-testid={`${testId}.InPath.${index}.${input.name}`}
                            onChange={(e) => setInput(index, input.name, { path: e.value })}
                          />
                        ) : mapped.source === 'LITERAL' ? (
                          <InputText
                            value={(mapped.value as string) || ''}
                            placeholder="value"
                            className="flex-1 text-sm"
                            data-testid={`${testId}.InValue.${index}.${input.name}`}
                            onChange={(e) => setInput(index, input.name, { value: e.target.value })}
                          />
                        ) : (
                          <InputText
                            value={mapped.path || ''}
                            placeholder="dotted path"
                            className="flex-1 text-sm font-mono"
                            data-testid={`${testId}.InPath.${index}.${input.name}`}
                            onChange={(e) => setInput(index, input.name, { path: e.target.value })}
                          />
                        )}
                      </div>
                    );
                  }) : <span className="text-xs text-gray-400">None declared.</span>}
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-xs text-ink-muted">
                    Outputs — name an alias to publish it to later steps
                  </label>
                  {outputs.length ? outputs.map((output) => (
                    <div key={output.name} className="flex items-center gap-1.5">
                      <span className="font-mono text-xs w-28 shrink-0 text-ink">
                        {output.name}{output.isCollection ? '[]' : ''}
                      </span>
                      <InputText
                        value={(step.outputMapping || {})[output.name] || ''}
                        placeholder="alias"
                        className="flex-1 text-sm font-mono"
                        data-testid={`${testId}.OutAlias.${index}.${output.name}`}
                        onChange={(e) => setOutputAlias(index, output.name, e.target.value)}
                      />
                    </div>
                  )) : <span className="text-xs text-gray-400">None declared.</span>}
                </div>
              </div>
            )}
          </div>
        );
      })}

      <div>
        <Button type="button" label="Add step" icon="pi pi-plus" size="small" severity="secondary" outlined
          disabled={!functions.length}
          onClick={() => onChange([...value, { functionMasterId: '', inputMapping: {}, outputMapping: {} }])}
          data-testid={`${testId}.Add`} />
      </div>
    </div>
  );
};

export default FunctionChainEditor;
